import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, CreditCard, Smartphone, Building } from "lucide-react";
import { Button } from "../../../../shadcn/ui/button";
import { Card, CardContent } from "../../../../shadcn/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../shadcn/ui/dialog";
import messageHandler from "../../../../utils/messageHandler";
import { getUser } from "../../../../services/localService";
import logger from "../../../../utils/logger";


const SavedPaymentMethods = ({ onAddNew, onEdit, canEdit = true, borderless = false }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);

      // Import payment service
      const { paymentService } = await import("../../../../services/paymentService");

      // Get organization ID from current user data
      const currentUser = getUser();
      const organizationId = currentUser?.organizationId || currentUser?.organization?.id;

      if (!organizationId) {
        throw new Error("No organization ID found for current user");
      }

      // Make API call to fetch saved payment methods
      const response = await paymentService.getOrganizationPaymentSetups(organizationId);

      logger.log("API Response:", response);
      logger.log("Response data:", response.data);
      logger.log("Is array:", Array.isArray(response.data));
      logger.log("Data length:", response.data?.length);

      // Handle different response structures
      let dataArray = null;
      if (response.data && Array.isArray(response.data)) {
        dataArray = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        dataArray = response.data.data;
      } else if (Array.isArray(response)) {
        dataArray = response;
      }

      logger.log("Data array:", dataArray);

      if (dataArray && dataArray.length > 0) {
        // Transform the API response to match our component's expected format
        const transformedData = dataArray.map((setup, index) => {
          // Map payment method types
          let type = "bank";
          if (setup.paymentMethod === "M_PESA_PAYBILL") {
            type = "paybill";
          } else if (setup.paymentMethod === "M_PESA_TILL") {
            type = "till";
          } else if (setup.paymentMethod === "BANK") {
            type = "bank";
          }

          // Create display name and details
          let name = setup.businessName || `Payment Method ${index + 1}`;
          let details = "";

          if (type === "bank") {
            details = `Account: ****${setup.accountNumber?.slice(-4) || "****"}`;
          } else if (type === "paybill") {
            details = `Paybill: ${setup.accountNumber || "****"}`;
          } else if (type === "till") {
            details = `Till: ${setup.accountNumber || "****"}`;
          }

          return {
            id: setup.id,
            type: type,
            name: name,
            details: details,
            status: setup.active ? "active" : "inactive",
            isPrimary: setup.primary || false,
            createdAt: setup.createdAt || new Date().toISOString(),
            // Store the full setup data for editing
            setupData: setup,
          };
        });

        logger.log("Transformed data:", transformedData);
        logger.log("Transformed data length:", transformedData.length);
        setPaymentMethods(transformedData);
      } else {
        // If no data, set empty array
        setPaymentMethods([]);
      }
    } catch (error) {
      logger.error("Error loading payment methods:", error);
      messageHandler.showApiMessage(error, "load payment methods");
      // Set empty array on error
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodIcon = type => {
    switch (type) {
      case "bank":
        return <CreditCard className="w-5 h-5" />;
      case "paybill":
        return <Smartphone className="w-5 h-5" />;
      case "till":
        return <Building className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  const getPaymentMethodType = type => {
    switch (type) {
      case "bank":
        return "Bank Account";
      case "paybill":
        return "M-Pesa Paybill";
      case "till":
        return "M-Pesa Till";
      default:
        return "Payment Method";
    }
  };

  const handleDeleteClick = method => {
    setPaymentMethodToDelete(method);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!paymentMethodToDelete) return;

    try {
      setDeleting(true);

      // Import payment service
      const { paymentService } = await import("../../../../services/paymentService");

      // Get organization ID from current user data
      const currentUser = getUser();
      const organizationId = currentUser?.organizationId || currentUser?.organization?.id;

      if (!organizationId) {
        throw new Error("No organization ID found for current user");
      }

      // Delete the payment setup
      const response = await paymentService.deletePaymentSetup(organizationId, paymentMethodToDelete.id);

      if (response.status === 204 || response.status === 200) {
        messageHandler.showApiMessage(response, "delete payment method");
        // Refresh the list
        await loadPaymentMethods();
        setDeleteDialogOpen(false);
        setPaymentMethodToDelete(null);
      } else {
        messageHandler.showApiMessage(response, "delete payment method");
      }
    } catch (error) {
      logger.error("Error deleting payment method:", error);
      messageHandler.showApiMessage(error, "delete payment method");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPaymentMethodToDelete(null);
  };

  if (loading) {
    return (
      <div className={borderless ? "" : "bg-white rounded-lg shadow-sm border"}>
        <div className={borderless ? "space-y-4 animate-pulse" : "p-6 animate-pulse"}>
          <div className="h-4 bg-slate-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-slate-200 rounded"></div>
            <div className="h-16 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={borderless ? "" : "bg-white rounded-lg shadow-sm border"}>
      {/* Header */}
      <div className={borderless ? "flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-gray-100" : "px-6 py-4 border-b"}>
        <div className="flex items-center justify-between w-full">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Saved Payment Methods</h3>
            <p className="text-sm text-gray-500 mt-0.5">Manage your payment methods for receiving payouts and booking earnings</p>
          </div>
          {canEdit && (
            <Button onClick={onAddNew} className="bg-blue-600 hover:bg-blue-700 text-white flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              Add New Method
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={borderless ? "" : "p-6"}>
        {paymentMethods.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Methods</h3>
            <p className="text-gray-600">You haven't set up any payment methods yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {paymentMethods.map(method => {
              const isInactive = method.status === "inactive";
              const isPrimary = method.isPrimary;

              return (
                <Card
                  key={method.id}
                  className="border border-gray-200 hover:border-blue-300 transition-colors bg-white mb-3"
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isInactive ? "bg-gray-100 text-gray-400" : "bg-blue-50 text-blue-600"
                          }`}
                        >
                          {getPaymentMethodIcon(method.type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-lg">{method.name}</h3>
                            {isPrimary && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                Primary
                              </span>
                            )}
                            {isInactive ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                Inactive
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                Active
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                            <p className="font-medium text-gray-700">{getPaymentMethodType(method.type)}</p>
                            <span className="hidden sm:inline text-gray-300">•</span>
                            <p className="text-gray-500">{method.details}</p>
                          </div>
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center space-x-2">
                          <div className="flex space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const editData = {
                                  ...method.setupData,
                                  type: method.type,
                                };
                                onEdit(editData);
                              }}
                              className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(method)}
                              className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Payment Method</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{paymentMethodToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedPaymentMethods;
